import api from './api';

// ======================================
// Type Definitions
// ======================================

export interface MenuItem {
  id: string;
  title: string;
  path: string;
  icon: string | null;
  order: number;
  isGroup: boolean;
  isActive: boolean;
  sortOrder: number;
  isDeleted: boolean;
  isSystem: boolean;
  deletedAt: string | null;
  parentId: string | null;
  resource?: string;
  permissions?: MenuPermissions;
  children?: MenuItem[];
  isExpanded?: boolean;
}

export interface MenuPermissions {
  view: string;
  create: string;
  edit: string;
  delete: string;
}

export interface RoleMenuPermission {
  id: string;
  roleId: string;
  menuId: string | null;
  resource: string | null;
  action: string | null;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export interface MenuWithPermissions extends Omit<MenuItem, 'permissions'> {
  resource: string;
  permissions: MenuPermissions;
}

// ======================================
// API Response Types
// ======================================

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ======================================
// Request Types
// ======================================

export interface CreateMenuDto {
  title: string;
  path: string;
  icon: string;
  order: number;
  isGroup: boolean;
  isActive: boolean;
  parentId: string | null;
}

export interface UpdateMenuDto extends Partial<CreateMenuDto> {
  parentId?: string | null;
  isDeleted?: boolean;
}

// ======================================
// API Functions
// ======================================

/**
 * Fetches all menu items with pagination and search
 * @param query - Query string with pagination and search parameters
 * @returns Promise with paginated menu items and pagination info
 */
export const getMenus = async (query: string = ''): Promise<PaginatedResponse<MenuItem>> => {
  const response = await api.get<{ data: PaginatedResponse<MenuItem> }>(
    `/menus?${query}`
  );
  return response.data.data;
};

/**
 * Fetches all menus with their hierarchy for the main navigation
 * @returns Promise with array of menu items including children
 */
export const getMenuTree = async (): Promise<MenuItem[]> => {
  const response = await api.get<{ data: MenuItem[] }>('/menus/tree');
  return response.data.data;
};

/**
 * Fetches all menus with permission strings for role editing
 * @returns Promise with array of menus including permission strings
 */
export const getMenusForRoleEdit = async (): Promise<MenuWithPermissions[]> => {
  const response = await api.get<{ data: MenuWithPermissions[] }>('/menus/for-role-edit');
  return response.data.data;
};

/**
 * Fetches menu permissions for a specific role
 * @param roleId - ID of the role to fetch permissions for
 * @returns Promise with array of role menu permissions
 */
export const getRoleMenuPermissions = async (roleId: string): Promise<RoleMenuPermission[]> => {
  const response = await api.get<{ data: RoleMenuPermission[] }>(
    `/menus/role-permissions/${roleId}`
  );
  return response.data.data;
};

/**
 * Fetches a menu item by ID
 * @param id - ID of the menu item to fetch
 * @returns Promise with the menu item
 */
const getMenuById = async (id: string): Promise<MenuItem> => {
  const response = await api.get<{ data: MenuItem }>(`/menus/${id}`);
  return response.data.data;
};

/**
 * Creates a new menu item
 * @param data - Menu item data
 * @returns Promise with the created menu item
 */
const createMenu = async (data: CreateMenuDto): Promise<MenuItem> => {
  const response = await api.post<{ data: MenuItem }>('/menus', data);
  return response.data.data;
};

/**
 * Updates a menu item
 * @param id - ID of the menu item to update
 * @param data - Updated menu item data
 * @returns Promise with the updated menu item
 */
const updateMenu = async (id: string, data: UpdateMenuDto): Promise<MenuItem> => {
  const response = await api.patch<{ data: MenuItem }>(`/menus/${id}`, data);
  return response.data.data;
};

/**
 * Deletes a menu item
 * @param id - ID of the menu item to delete
 */
const deleteMenu = async (id: string): Promise<void> => {
  await api.delete(`/menus/${id}`);
};

/**
 * Fetches all menu items in a flat list (for admin interface)
 * @returns Promise with array of all menu items
 */
const getAllFlatMenus = async (): Promise<MenuItem[]> => {
  const response = await api.get<{ data: MenuItem[] }>('/menus/all');
  return response.data.data;
};

/**
 * Fetches menu permissions
 * @returns Promise with array of menu permissions
 */
const getMenuPermissions = async (): Promise<Array<{
  id: string;
  title: string;
  path: string;
  icon: string | null;
  order: number;
  parentId: string | null;
  isGroup: boolean;
  isSystem: boolean;
  isActive: boolean;
  permissions?: {
    view: string;
    create: string;
    edit: string;
    delete: string;
  };
  children?: any[];
}>> => {
  const response = await api.get<{ data: any }>('/menus/permissions');
  return response.data.data;
};

/**
 * Updates menu permissions for a role
 * @param roleId - ID of the role to update permissions for
 * @param permissions - Array of permission updates
 * @returns Promise with success status
 */
const updateRoleMenuPermissions = async (
  roleId: string,
  permissions: Array<{
    menuId: string;
    canView: boolean;
    canEdit: boolean;
    canDelete: boolean;
  }>
): Promise<boolean> => {
  const response = await api.patch<{ success: boolean }>(
    `/menus/role-permissions/${roleId}`,
    { permissions }
  );
  return response.data.success;
};

// ======================================
// API Export
// ======================================

export const menuApi = {
  // Menu items
  items: {
    getAll: getMenus,
    getTree: getMenuTree,
    getForRoleEdit: getMenusForRoleEdit,
    getById: getMenuById,
    create: createMenu,
    update: updateMenu,
    delete: deleteMenu,
    getAllFlat: getAllFlatMenus,
    getMenuPermissions,
  },
  
  // Menu permissions
  permissions: {
    getByRole: getRoleMenuPermissions,
    updateForRole: updateRoleMenuPermissions,
  },
};

export default menuApi;
